// ============================================================================
// FSA SIGNATURE WORKFLOW TYPES
// ============================================================================
// Mirrors prima.Core.DTOs.FsaModule.Signature.* on the API side.
// See projects/prima/prima-doc/FSA-Signature-Workflow.md for the data model.

import type { ID } from '../index';

// Stronger string ID for grid compatibility (SyutGrid requires `id: string`).
type Uuid = string;

// ---------------------------------------------------------------------------
// Audit Firm
// ---------------------------------------------------------------------------

export interface AuditFirm {
  id: Uuid;
  firmName: string;
  firmRegistrationNumber: string;
  reportAttestationText?: string | null;
  signatureBlockText?: string | null;
  address?: string | null;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  logoPath?: string | null;
  partnerCount: number;
  statusId: ID;
  statusCode: string;
  statusName: string;
  statusColor?: string | null;
  rowVersion: number;
  createdAt: string;
  createdBy?: ID | null;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedBy?: ID | null;
  updatedByName?: string | null;
}

export interface CreateAuditFirmRequest {
  firmName: string;
  firmRegistrationNumber: string;
  reportAttestationText?: string | null;
  signatureBlockText?: string | null;
  address?: string | null;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  logoPath?: string | null;
  statusCode?: string;
}

export interface UpdateAuditFirmRequest extends CreateAuditFirmRequest {
  rowVersion: number;
}

export interface AuditFirmSearchRequest {
  page?: number;
  pageSize?: number;
  searchTerm?: string;
  firmName?: string;
  firmRegistrationNumber?: string;
  statusCode?: string;
  includeInactive?: boolean;
}

// ---------------------------------------------------------------------------
// Audit Partner
// ---------------------------------------------------------------------------

export interface AuditPartner {
  id: Uuid;
  firmId: ID;
  firmName?: string | null;
  firmRegistrationNumber?: string | null;
  partnerName: string;
  membershipNumber: string;
  designation: string;
  signatureBlockText?: string | null;
  defaultUdinFormat?: string | null;
  signatureImagePath?: string | null;
  statusId: ID;
  statusCode: string;
  statusName: string;
  statusColor?: string | null;
  rowVersion: number;
  createdAt: string;
  createdBy?: ID | null;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedBy?: ID | null;
  updatedByName?: string | null;
}

export interface CreateAuditPartnerRequest {
  firmId: ID;
  partnerName: string;
  membershipNumber: string;
  designation: string;
  signatureBlockText?: string | null;
  defaultUdinFormat?: string | null;
  signatureImagePath?: string | null;
  statusCode?: string;
}

export interface UpdateAuditPartnerRequest extends CreateAuditPartnerRequest {
  rowVersion: number;
}

export interface AuditPartnerSearchRequest {
  page?: number;
  pageSize?: number;
  searchTerm?: string;
  firmId?: ID;
  partnerName?: string;
  membershipNumber?: string;
  statusCode?: string;
  includeInactive?: boolean;
}

// ---------------------------------------------------------------------------
// Director Signatory
// ---------------------------------------------------------------------------

export interface DirectorSignatory {
  id: Uuid;
  clientId: ID;
  clientLegalName?: string | null;
  clientCode?: string | null;
  signatoryName: string;
  din?: string | null;
  designation: string;
  signatureBlockText?: string | null;
  effectiveFrom: string;
  effectiveTo?: string | null;
  signatureImagePath?: string | null;
  statusId: ID;
  statusCode: string;
  statusName: string;
  statusColor?: string | null;
  rowVersion: number;
  createdAt: string;
  createdBy?: ID | null;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedBy?: ID | null;
  updatedByName?: string | null;
}

export interface CreateDirectorSignatoryRequest {
  clientId: ID;
  signatoryName: string;
  din?: string | null;
  designation: string;
  signatureBlockText?: string | null;
  effectiveFrom: string;
  effectiveTo?: string | null;
  signatureImagePath?: string | null;
  statusCode?: string;
}

export interface UpdateDirectorSignatoryRequest extends CreateDirectorSignatoryRequest {
  rowVersion: number;
}

export interface DirectorSignatorySearchRequest {
  page?: number;
  pageSize?: number;
  searchTerm?: string;
  clientId?: ID;
  signatoryName?: string;
  designation?: string;
  eligibleAsOf?: string;
  statusCode?: string;
  includeInactive?: boolean;
}

// ---------------------------------------------------------------------------
// Engagement Signature Panel
// ---------------------------------------------------------------------------

export type SignatoryRole = 'AUDITOR_PARTNER' | 'DIRECTOR' | 'OTHER';

export interface EngagementSignaturePanelSignatory {
  id: Uuid;
  panelId: ID;
  role: SignatoryRole;
  signatoryMasterId?: ID | null;
  columnPosition?: number | null;
  displayOrder: number;
  udinNumber?: string | null;
  nameSnapshot: string;
  designationSnapshot: string;
  dinOrMembershipSnapshot?: string | null;
  signatureBlockTextSnapshot?: string | null;
  signatureImagePathSnapshot?: string | null;
  rowVersion: number;
  createdAt: string;
  updatedAt?: string | null;
}

export interface EngagementSignaturePanel {
  id: Uuid;
  primaEngagementId: ID;
  engagementCode?: string | null;
  clientLegalName?: string | null;
  auditFirmId: ID;
  auditFirmName?: string | null;
  auditFirmRegistrationNumber?: string | null;
  auditFirmReportAttestationText?: string | null;
  place?: string | null;
  reportDate?: string | null;
  notes?: string | null;
  signatories: EngagementSignaturePanelSignatory[];
  statusId: ID;
  statusCode: string;
  statusName: string;
  statusColor?: string | null;
  rowVersion: number;
  createdAt: string;
  updatedAt?: string | null;
}

export interface UpsertEngagementSignaturePanelSignatoryItem {
  role: SignatoryRole;
  signatoryMasterId?: ID | null;
  columnPosition?: number | null;
  displayOrder: number;
  /** 18-character ICAI UDIN (per partner per engagement). */
  udinNumber?: string | null;
  /** Free-text overrides for OTHER-role signatories. Ignored when signatoryMasterId is set. */
  nameOverride?: string | null;
  designationOverride?: string | null;
  dinOrMembershipOverride?: string | null;
  signatureBlockTextOverride?: string | null;
}

export interface UpsertEngagementSignaturePanelRequest {
  primaEngagementId: ID;
  auditFirmId: ID;
  place?: string | null;
  reportDate?: string | null;
  notes?: string | null;
  signatories: UpsertEngagementSignaturePanelSignatoryItem[];
  rowVersion: number;
  statusCode?: string;
}

/** ICAI UDIN format — 18 chars: 17 digits + 2 alpha + 4 digits + 2 alpha + 4 digits ... etc. */
export const UDIN_REGEX = /^\d{17}[A-Z]{2}\d{4}[A-Z]{2}\d{4}$/;
